/**
 * 共通の外殻(UX1-a)。
 *
 * 左から アイコンレール 64px(アプリ切替・管理・ユーザー)/ アプリ内ナビ 232px /
 * 中身(上に 52px のコマンドバー: パンくず・検索(⌘K)・お知らせ・ユーザー名)。
 * 色は `theme.ts` のトークン(`--ux-*`)だけを使い、ここに色を書かない。
 */
import {
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Drawer, Dropdown, Menu, Space, Tooltip } from "antd";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { hasRole, isAnyAdmin } from "../lib/roles";
import { useApi, useMe } from "../lib/useApi";
import { appIcon } from "./appIcons";
import { CommandPalette, type PaletteEntry } from "./CommandPalette";
import { signOut } from "../lib/auth";
import { HelpChatButton } from "./help/HelpChatButton";
import { ManualButton } from "./help/ManualButton";
import { useManualIndex, useManualSession } from "./help/manualHooks";
import { visibleGroups, type AppDef } from "./nav";
import { useNotifications } from "./notificationHooks";
import { DeleteReadButton, NotificationList, UnreadBell } from "./notifications";
import "./shell.css";

const SUITE_NAME = "統合IT管理基盤";

type NavModel = {
  /** ナビ列の見出し(アプリ名) */
  title: string;
  /** antd Menu に渡す群 */
  groups: { label: string; items: { key: string; label: ReactNode }[] }[];
  /** 選択中の key(フルパス) */
  selected: string | undefined;
  /** パンくず。先頭がアプリ、末尾が現在地 */
  crumbs: { label: string; to?: string }[];
};

function RailButton({
  label,
  icon,
  selected,
  onClick,
  testId,
}: {
  label: string;
  icon: ReactNode;
  selected?: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <Tooltip title={label} placement="right">
      <button
        type="button"
        className={`ux-rail-btn${selected ? " is-selected" : ""}`}
        aria-label={label}
        aria-current={selected ? "page" : undefined}
        data-testid={testId}
        onClick={onClick}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

export function AppShell({ apps: appDefs }: { apps: AppDef[] }) {
  const auth = useAuth();
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const manual = useManualIndex();
  useManualSession(manual.data?.view);

  const apps = useQuery({ queryKey: ["apps"], queryFn: api.apps, staleTime: 60_000 });
  const notifications = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const section = location.pathname.split("/")[1] || "portal";
  const availableApps = (apps.data?.items ?? []).filter((a) => a.status === "available");
  const unread = notifications.data?.unread ?? 0;

  const canSeeAudit = isAnyAdmin(me.data);
  const adminItems = [
    ...(canSeeAudit ? [{ key: "/admin/audit", label: "監査ログ" }] : []),
    // 管理者操作ログは「どれかのアプリの admin」なら見られる(R7c fix1 低1)が、
    // 画面への入口がどこにも無く URL 直打ちしか手が無かった(R7c fix2 低4)。
    // idp のロールが無い admin(hr だけの admin など)からも辿れるよう、
    // アプリのメニューではなくここ(アプリ横断の「管理」)に置く
    ...(canSeeAudit ? [{ key: "/idp/logs/admin-ops", label: "管理者操作ログ" }] : []),
    ...(me.data?.is_suite_admin ? [{ key: "/admin/settings", label: "設定" }] : []),
    // ライセンス(契約している機能)の確認と貼り替え。suite:admin だけ
    ...(me.data?.is_suite_admin ? [{ key: "/license", label: "ライセンス" }] : []),
  ];
  const isAdminPage = adminItems.some((it) => it.key === location.pathname);

  // ---- アプリ内ナビの中身(いまの場所で決まる) ----------------------------
  const nav: NavModel = useMemo(() => {
    if (section === "manual") {
      const current = manual.data?.modules.find((m) => m.module === location.pathname.split("/")[2]);
      return {
        title: "マニュアル",
        groups: [{ label: "", items: [{ key: "/manual", label: "マニュアル一覧" },
          ...(manual.data?.modules.map((m) => ({ key: `/manual/${m.module}`, label: m.label })) ?? [])] }],
        selected: location.pathname,
        crumbs: [{ label: "マニュアル", to: current ? "/manual" : undefined },
          ...(current ? [{ label: current.label }] : [])],
      };
    }
    const def = appDefs.find((d) => d.key === section);
    // 管理の画面(`/idp/logs/admin-ops` など、アプリの path の下にあるもの)はアプリより先に判定する。
    // 後にすると idp のナビが出てレールの ID管理が点く(UX1-a 低10)
    if (!isAdminPage && def && me.data?.apps.includes(def.key)) {
      const groups = visibleGroups(def, me.data);
      if (def.key === "dlp") groups.push({ label: "メールの処理", items: [
        { path: "mail/my", label: "自分のメール", phase: "R6c", feature: "dlp.mail" },
        { path: "mail/approvals", label: "割当承認", phase: "R6c", feature: "dlp.mail" },
        ...(hasRole(me.data, "dlp:viewer") ? [{ path: "mail/messages", label: "メール管理・ドレイン", phase: "R6c", feature: "dlp.mail" }] : []),
      ] });
      const rel = location.pathname.replace(`/${def.key}`, "").replace(/^\//, "");
      // 詳細ページ(employees/123)でも親の項目を点灯させる: いちばん長い前方一致
      const items = groups.flatMap((g) => g.items);
      const hit = items
        .filter((it) => rel === it.path || rel.startsWith(`${it.path}/`))
        .sort((a, b) => b.path.length - a.path.length)[0];
      return {
        title: def.name,
        groups: groups.map((g) => ({
          label: g.label,
          items: g.items.map((it) => ({ key: `/${def.key}/${it.path}`, label: it.label })),
        })),
        selected: hit ? `/${def.key}/${hit.path}` : undefined,
        crumbs: [{ label: def.name, to: `/${def.key}` }, ...(hit ? [{ label: hit.label }] : [])],
      };
    }
    if (isAdminPage) {
      const hit = adminItems.find((it) => it.key === location.pathname);
      return {
        title: "管理",
        groups: [{ label: "管理", items: adminItems.map((it) => ({ key: it.key, label: it.label })) }],
        selected: location.pathname,
        crumbs: [{ label: "管理" }, ...(hit ? [{ label: hit.label }] : [])],
      };
    }
    const home = [
      { key: "/portal", label: "ホーム" },
      {
        key: "/notifications",
        label: (
          <span className="ux-nav-row">
            <span>お知らせ</span>
            {unread > 0 && <span className="ux-nav-badge">{unread > 99 ? "99+" : unread}</span>}
          </span>
        ),
      },
      { key: "/dlp/mail/my", label: "自分のメール" },
      { key: "/dlp/mail/approvals", label: "割当承認" },
      ...(me.data && hasRole(me.data, "dlp:approver") ? [{ key: "/dlp/mail/messages", label: "メール管理・ドレイン" }] : []),
      { key: "/portal#account", label: "自分のアカウント" },
      { key: "/portal#sso", label: "SSO サービス" },
    ];
    const selected =
      location.pathname === "/portal"
        ? location.hash
          ? `/portal${location.hash}`
          : "/portal"
        : location.pathname === "/notifications"
          ? "/notifications"
          : undefined;
    const tail =
      location.pathname === "/notifications"
        ? [{ label: "お知らせ" }]
        : section === "dlp"
          ? [{ label: availableApps.find((a) => a.key === "dlp")?.name ?? "DLP" }]
          : [];
    return {
      title: SUITE_NAME,
      groups: [{ label: "", items: home }],
      selected,
      crumbs: [{ label: "ホーム", to: tail.length ? "/portal" : undefined }, ...tail],
    };
    // adminItems は me.data から毎回作り直すので、依存は me.data と場所で足りる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDefs, section, location.pathname, location.hash, me.data, unread, isAdminPage, apps.data, manual.data]);

  // ---- ⌘K の候補(いま出せる項目だけ) ---------------------------------------
  const paletteEntries: PaletteEntry[] = useMemo(() => {
    const rows: PaletteEntry[] = [
      { label: "ホーム", group: SUITE_NAME, to: "/portal" },
      { label: "お知らせ", group: SUITE_NAME, to: "/notifications" },
      { label: "マニュアル", group: "共通", to: "/manual" },
    ];
    for (const def of appDefs) {
      if (!me.data?.apps.includes(def.key)) continue;
      for (const g of visibleGroups(def, me.data)) {
        for (const it of g.items) {
          rows.push({ label: it.label, group: `${def.name} › ${g.label}`, to: `/${def.key}/${it.path}` });
        }
      }
    }
    for (const it of adminItems) rows.push({ label: it.label, group: "管理", to: it.key });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDefs, me.data]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.metaKey || ev.ctrlKey) && !ev.altKey && ev.key.toLowerCase() === "k") {
        ev.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (ev.key === "Escape" && !ev.isComposing && ev.keyCode !== 229) {
        // 入力欄にフォーカスが無くても閉じる(閉じる途中で開き直すと autoFocus が
        // 効かず、パレット側の onKeyDown に Escape が届かない)。
        // IME 変換を取り消す Escape は変換の操作なので見送る(パレット側と同じガード)
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // 場所が変わったら畳んだナビ(Drawer)は閉じる
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.hash]);

  // 契約の異常は**全画面で**知らせる。黙って機能が消えると原因が分からない
  const lic = me.data?.license;
  const licenseWarning =
    lic && lic.state !== "ok"
      ? {
          type: (lic.state === "grace" ? "warning" : "error") as "warning" | "error",
          text:
            lic.state === "grace"
              ? `ライセンスの期限が切れている(猶予中: ${lic.grace_until?.slice(0, 10) ?? ""} まで)。更新してください。`
              : lic.state === "expired"
                ? "ライセンスの期限が切れている。規則・設定は参照のみ。メール DLP の判定とドレイン操作は継続します。"
                : lic.state === "missing"
                  ? "ライセンスが入っていない。新規機能は停止中。既存メールのドレイン操作は使えます。"
                  : `ライセンスが不正: ${lic.problem}`,
        }
      : null;
  const licenseFoot =
    lic && lic.state === "ok"
      ? (
          <>
            <div>ライセンス: 有効</div>
            {lic.expires_at && <div>{lic.expires_at.slice(0, 10)} まで</div>}
          </>
        )
      : null;

  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const userName = me.data?.display_name || me.data?.username || "";
  const initial = userName ? [...userName][0] : "";

  const menuItems = nav.groups.map((g, gi) =>
    g.label
      ? { key: `g:${g.label}`, label: g.label, type: "group" as const, children: g.items }
      : { key: `g:${gi}`, type: "group" as const, children: g.items },
  );

  const ctxNav = (
    <nav className="ux-ctxnav" aria-label={`${nav.title}のメニュー`}>
      <div className="ux-ctxnav-title">{nav.title}</div>
      <Menu
        mode="inline"
        selectedKeys={nav.selected ? [nav.selected] : []}
        items={menuItems}
        onClick={({ key }) => navigate(key)}
      />
      {licenseFoot && <div className="ux-ctxnav-foot">{licenseFoot}</div>}
    </nav>
  );

  return (
    <div className="ux-shell">
      <nav className="ux-rail" aria-label="アプリ">
        <Link to="/portal" className="ux-rail-logo" aria-label={SUITE_NAME}>
          IT
        </Link>
        <RailButton
          label="ホーム"
          icon={<HomeOutlined />}
          selected={section === "portal" || section === "notifications"}
          onClick={() => navigate("/portal")}
        />
        {availableApps.map((a) => (
          <RailButton
            key={a.key}
            label={a.name}
            icon={appIcon(a.key)}
            selected={section === a.key && !isAdminPage}
            onClick={() => navigate(a.path)}
            testId={`rail-${a.key}`}
          />
        ))}
        <div className="ux-rail-spacer" />
        {adminItems.length > 0 && (
          <Dropdown
            menu={{ items: adminItems, onClick: ({ key }) => navigate(key) }}
            placement="topLeft"
          >
            <Tooltip title="管理" placement="right">
              <button
                type="button"
                className={`ux-rail-btn${isAdminPage ? " is-selected" : ""}`}
                aria-label="管理"
                data-testid="admin-menu"
              >
                <SettingOutlined />
              </button>
            </Tooltip>
          </Dropdown>
        )}
        <Dropdown
          placement="topLeft"
          menu={{
            items: [
              {
                key: "logout",
                icon: <LogoutOutlined />,
                label: "ログアウト",
                onClick: () => void signOut(auth),
              },
            ],
          }}
        >
          <Tooltip title={userName || "ユーザー"} placement="right">
            <button type="button" className="ux-rail-avatar" aria-label={userName || "ユーザー"}>
              {initial}
            </button>
          </Tooltip>
        </Dropdown>
      </nav>

      {ctxNav}

      <div className="ux-main">
        <header className="ux-cmdbar">
          <button
            type="button"
            className="ux-iconbtn ux-navtoggle"
            aria-label="メニューを開く"
            onClick={() => setNavOpen(true)}
          >
            <MenuOutlined />
          </button>
          <nav className="ux-crumbs" aria-label="パンくず">
            {nav.crumbs.map((c, i) => (
              <span key={`${c.label}:${i}`} className="ux-crumbs-seg">
                {i > 0 && <span aria-hidden="true"> › </span>}
                {c.to ? (
                  <Link to={c.to}>{c.label}</Link>
                ) : (
                  <span className={i === nav.crumbs.length - 1 ? "is-current" : undefined}>
                    {c.label}
                  </span>
                )}
              </span>
            ))}
          </nav>
          <div className="ux-cmdbar-spacer" />
          <button
            type="button"
            className="ux-search"
            aria-label="画面を検索"
            onClick={() => setPaletteOpen(true)}
          >
            <SearchOutlined />
            <span>画面を検索</span>
            <kbd className="ux-kbd">{isMac ? "⌘K" : "Ctrl K"}</kbd>
          </button>
          <ManualButton />
          <HelpChatButton />
          <button
            type="button"
            className="ux-iconbtn"
            data-testid="notif-bell"
            aria-label="お知らせ"
            onClick={() => setNotifOpen(true)}
          >
            <UnreadBell unread={unread} />
          </button>
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: "logout",
                  icon: <LogoutOutlined />,
                  label: "ログアウト",
                  onClick: () => void signOut(auth),
                },
              ],
            }}
          >
            <button type="button" className="ux-user">
              <span className="ux-user-name">{userName || "…"}</span>
            </button>
          </Dropdown>
        </header>

        {licenseWarning && (
          <Alert
            data-testid="license-banner"
            type={licenseWarning.type}
            showIcon
            banner
            message={licenseWarning.text}
            action={
              me.data?.is_suite_admin ? (
                <Button size="small" onClick={() => navigate("/license")}>
                  ライセンス
                </Button>
              ) : undefined
            }
          />
        )}

        <div className="ux-content">
          <Outlet />
        </div>
      </div>

      <Drawer
        title="お知らせ"
        open={notifOpen}
        width={420}
        onClose={() => setNotifOpen(false)}
        extra={
          <Space size={8}>
            <DeleteReadButton
              disabled={(notifications.data?.total ?? 0) <= (notifications.data?.unread ?? 0)}
            />
            <Button
              size="small"
              onClick={() => {
                setNotifOpen(false);
                navigate("/notifications");
              }}
            >
              すべて見る
            </Button>
          </Space>
        }
      >
        <NotificationList
          rows={notifications.data?.items ?? []}
          loading={notifications.isFetching && !notifications.data}
          onNavigate={() => setNotifOpen(false)}
        />
      </Drawer>

      {/* 1280 未満ではアプリ内ナビを畳み、ここから開く(中身は同じ) */}
      <Drawer
        placement="left"
        open={navOpen}
        width={264}
        onClose={() => setNavOpen(false)}
        rootClassName="ux-navdrawer"
        styles={{ body: { padding: 12 } }}
      >
        {navOpen && ctxNav}
      </Drawer>

      <CommandPalette
        open={paletteOpen}
        entries={paletteEntries}
        onClose={() => setPaletteOpen(false)}
      />
    </div>
  );
}
